import { useEffect, useMemo, useState } from 'react'
import { apiURL } from '../utilities/URLs'
import { CustomerCouponCode, customerData } from '../utilities/CustomerData'
import { getChargeableFullBalance } from '../utilities/paymentAmount'
import {
  getInstallmentPaymentCountOptions,
  getLatestMonthlyInstallmentStartDate,
  getMaxMonthlyInstallmentsFromToday,
  getMaxMonthlyInstallmentPayments,
  getMonthlyInstallmentDate,
  MAX_WEEKLY_INSTALLMENT_PAYMENTS
} from '../utilities/paymentInstallments'
import { refreshAccessToken, shouldShowPaymentInstallmentTile } from '../utilities/extensions'
import { useNavigate } from 'react-router-dom'
import CreditCardInput from 'react-credit-card-input'
import DatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'

export default function PaymentInstallations() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!shouldShowPaymentInstallmentTile())
      navigate('/dashboard')
  }, [navigate])

  const [needCardInfo, setNeedCardInfo] = useState(true)
  const [cardInfo, setCardInfo] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  })
  const [errors, setErrors] = useState({})
  const [updatingCardInfo, setUpdatingCardInfo] = useState(false)
  const [error, setError] = useState('')
  const [paymentType, setPaymentType] = useState('')
  const [numPayments, setNumPayments] = useState('')
  const [paymentDate, setPaymentDate] = useState('')
  const [uploadingPaymentInstallations, setUploadingPaymentInstallations] = useState(false)
  const [uploadedPaymentInstallations, setUploadedPaymentInstallations] = useState(false)

  const [showCouponCode, setShowCouponCode] = useState(false)
  const [couponCodeInput, setCouponCodeInput] = useState('')
  const [customerCouponCode, setCustomerCouponCode] = useState(customerData.customerCouponCode)
  const [couponError, setCouponError] = useState('')

  const chargeableTotal = useMemo(
    () => getChargeableFullBalance(
      customerData.customerInvoiceData,
      customerCouponCode,
      customerData.customerReferralCode
    ),
    [customerCouponCode, customerData.customerInvoiceData, customerData.customerReferralCode]
  )

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)

  const monthlyStartMaxDate = getLatestMonthlyInstallmentStartDate()
  const paymentCountOptions = useMemo(
    () => getInstallmentPaymentCountOptions(paymentType, paymentDate || null),
    [paymentType, paymentDate]
  )
  const maxMonthlyPayments = paymentType === 'monthly'
    ? (paymentDate
      ? getMaxMonthlyInstallmentPayments(paymentDate)
      : getMaxMonthlyInstallmentsFromToday())
    : MAX_WEEKLY_INSTALLMENT_PAYMENTS

  useEffect(() => {
    if (!numPayments || paymentCountOptions.length === 0)
      return
    if (!paymentCountOptions.includes(Number(numPayments)))
      setNumPayments('')
  }, [numPayments, paymentCountOptions])

  const handleCardNumberChange = (e) => {
    setCardInfo({ ...cardInfo, cardNumber: e.target.value })
  }

  const handleCardExpiryChange = (e) => {
    setCardInfo({ ...cardInfo, expiryDate: e.target.value })
  }

  const handleCardCVCChange = (e) => {
    setCardInfo({ ...cardInfo, cvv: e.target.value })
  }

  const handleUpdateCardInfo = async () => {
    const newErrors = {}

    if (!cardInfo.cardNumber) newErrors.cardNumber = 'Card Number is required'
    if (!cardInfo.expiryDate) newErrors.expiryDate = 'Expiry Date is required'
    if (!cardInfo.cvv) newErrors.cvv = 'CVV is required'

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0)
      return

    setUpdatingCardInfo(true)
    const form = new FormData()
    form.append('cardInfo', JSON.stringify(cardInfo))

    const updateCardResponse = await fetch(`${apiURL}/api/updateCardInfo?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const updateCardData = await updateCardResponse.json()
    if (updateCardData.response.error) {
      if (updateCardData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleUpdateCardInfo() : navigate('/', { state: { expired: true } })
      } else {
        console.error(updateCardData.response.error)
        setError(updateCardData.response.error)
      }
    } else
      setNeedCardInfo(false)

    setUpdatingCardInfo(false)
  }

  const handleCheckCouponCode = async () => {
    const form = new FormData()
    form.append('couponInfo', JSON.stringify({
      customerId: customerData.customerLeadData.leadContact.contactId,
      couponCode: couponCodeInput,
      couponType: customerData.customerInvoiceData.invoiceTerms === 'Invoice Due On Receipt' ? 'Pre-Paid' : 'Post-Paid',
      currentLeadStageId: customerData.customerLeadData.leadStage.leadStageId
    }))

    const checkCouponResponse = await fetch(`${apiURL}/api/checkCouponCode`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const checkCouponData = await checkCouponResponse.json()
    if (checkCouponData.response.error) {
      if (checkCouponData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleCheckCouponCode() : navigate('/', { state: { expired: true } })
      } else
        setCouponError(checkCouponData.response.error)
    } else {
      setCouponError('')
      setCouponCodeInput('')
      const applied = new CustomerCouponCode({
        customerId: customerData.customerLeadData.leadContact.contactId,
        couponCode: checkCouponData.response.couponCode.couponCode,
        couponAmount: checkCouponData.response.couponCode.couponAmount,
        couponType: checkCouponData.response.couponCode.couponType
      })
      setCustomerCouponCode(applied)
      customerData.customerCouponCode = applied
    }
  }

  const handleUploadPaymentInstallations = async () => {
    setUploadingPaymentInstallations(true)

    const form = new FormData()
    form.append('paymentInstallations', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      paymentType,
      numPayments,
      paymentDate
    }))

    const uploadPaymentInstallationsResponse = await fetch(`${apiURL}/api/paymentInstallations/`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const uploadPaymentInstallationsData = await uploadPaymentInstallationsResponse.json()
    if (uploadPaymentInstallationsData.response.error) {
      if (uploadPaymentInstallationsData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleUploadPaymentInstallations() : navigate('/', { state: { expired: true } })
      } else
        console.error(uploadPaymentInstallationsData.response.error)
    } else
      setUploadedPaymentInstallations(true)

    setUploadingPaymentInstallations(false)
  }

  return (
    <div className='flex flex-col items-center w-full px-4 py-8'>
      <p className='text-3xl font-bold text-center mb-6'>Payment Installments</p>

      {needCardInfo ? (
        <div className='bg-white shadow-lg rounded-xl p-6 w-full max-w-lg'>
          <p className='text-center text-gray-800 font-medium mb-4'>Before we begin, we need a new card on file to debit your payments.</p>
          <div className='space-y-5'>
            <div>
              <label htmlFor='cardNumber' className='block text-sm font-medium text-gray-700 mb-1'>
                Card Information
              </label>
              <CreditCardInput
                cardNumberInputProps={{
                  value: cardInfo.cardNumber,
                  onChange: handleCardNumberChange,
                }}
                cardExpiryInputProps={{
                  value: cardInfo.expiryDate,
                  onChange: handleCardExpiryChange,
                }}
                cardCVCInputProps={{
                  value: cardInfo.cvv,
                  onChange: handleCardCVCChange,
                }}
                fieldClassName='p-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition'
                containerClassName='w-full space-y-2'
              />
            </div>
            <div className='text-xs text-red-500 space-y-1'>
              {errors.cardNumber && <p>{errors.cardNumber}</p>}
              {errors.expiryDate && <p>{errors.expiryDate}</p>}
              {errors.cvv && <p>{errors.cvv}</p>}
            </div>
            {error && (
              <p className='text-sm text-red-500'>{error}</p>
            )}
            <button
              type='submit'
              onClick={handleUpdateCardInfo}
              className='w-full text-white p-2 rounded-md font-semibold bg-primaryColor hover:bg-primaryDarkColor transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed'
              disabled={updatingCardInfo || !cardInfo.cardNumber || !cardInfo.cvv || !cardInfo.expiryDate}
            >
              {updatingCardInfo ? 'Updating Card...' : 'Update Card'}
            </button>
          </div>
        </div>
      ) : (
        !uploadedPaymentInstallations ? (
          <div className='bg-white shadow-lg rounded-xl p-6 w-full max-w-lg'>
            <p
              onClick={() => setShowCouponCode(!showCouponCode)}
              className='w-full p-4 text-white mb-4 rounded-lg shadow-lg cursor-pointer bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-center'
            >
              Have a coupon code? Click here to enter your code
            </p>
            {showCouponCode && (
              <div className='bg-blue-50 shadow-lg rounded-md p-4 mb-4'>
                <label htmlFor='couponCodeInstallments' className='block text-sm font-medium text-gray-700 mb-2'>
                  Apply Coupon Code
                </label>
                {couponError && <p className='text-red-500 text-xs mt-2'>{couponError}</p>}
                {customerCouponCode.couponCode.length > 0 ? (
                  <div className='flex flex-row gap-4 items-center mt-4'>
                    <p>Applied Coupon Code:</p>
                    <p className='font-bold'>{customerCouponCode.couponCode}</p>
                  </div>
                ) : (
                  <div className='flex items-center'>
                    <input
                      type='text'
                      id='couponCodeInstallments'
                      name='couponCodeInstallments'
                      value={couponCodeInput}
                      onChange={(e) => setCouponCodeInput(e.target.value)}
                      className='p-2 border border-gray-300 rounded-l-md w-full'
                      placeholder='Enter coupon code'
                    />
                    <button
                      type='button'
                      onClick={() => handleCheckCouponCode()}
                      className={`text-white p-2 rounded-r-md ${couponCodeInput.length === 0 ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 transition-colors'}`}
                      disabled={couponCodeInput.length === 0}
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>
            )}
            <label className='block text-sm font-medium text-gray-700 mb-2'>Payment Type</label>
            <select
              value={paymentType}
              onChange={(e) => {
                setPaymentType(e.target.value)
                setNumPayments('')
              }}
              className='w-full p-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value=''>--- SELECT PAYMENT TYPE ---</option>
              <option value='weekly'>Weekly</option>
              <option value='monthly'>Monthly</option>
            </select>
            {paymentType && (
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Number of Payments ({paymentType === 'weekly' ? `1-${MAX_WEEKLY_INSTALLMENT_PAYMENTS}` : maxMonthlyPayments > 0 ? `1-${maxMonthlyPayments}` : 'unavailable'})
                </label>
                {paymentType === 'monthly' && maxMonthlyPayments <= 0 ? (
                  <p className='text-sm text-red-500'>
                    Monthly installments are not available after December. Please choose a weekly plan instead.
                  </p>
                ) : (
                  <select
                    value={numPayments}
                    onChange={(e) => setNumPayments(e.target.value)}
                    className='w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  >
                    <option value=''>Select number...</option>
                    {paymentCountOptions.map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            {paymentType && numPayments && (
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Starting Date
                </label>
                <DatePicker
                  selected={paymentDate}
                  onChange={(date) => setPaymentDate(date)}
                  dateFormat='MMMM d, yyyy'
                  className='w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  minDate={tomorrow}
                  maxDate={paymentType === 'monthly' ? monthlyStartMaxDate : undefined}
                  filterDate={paymentType === 'monthly'
                    ? (date) => date.getDate() >= 1 && date.getDate() <= 28
                    : undefined
                  }
                  placeholderText={
                    paymentType === 'monthly'
                      ? 'Choose a date (1st-28th only)'
                      : 'Choose a start date'
                  }
                  wrapperClassName='w-full'
                />
              </div>
            )}
            {paymentType && numPayments && paymentDate && (
              <div className='mt-4'>
                <p className='text-sm text-gray-600 mb-3'>
                  You selected <span className='font-semibold'>{paymentType}</span> payments,
                  <span className='font-semibold'> {numPayments}</span> total, starting on{' '}
                  <span className='font-semibold'>
                    {paymentDate.toLocaleDateString()}
                  </span>.
                </p>
                <div className='border-t pt-3 mt-3'>
                  <p className='text-sm font-medium text-gray-700 mb-2'>Payment Schedule:</p>
                  <ul className='space-y-1 text-sm text-gray-600'>
                    {Array.from({ length: Number(numPayments) }, (_, i) => {
                      const amount = (chargeableTotal / Number(numPayments)).toFixed(2)
                      const originalAmount = (Number(customerData.customerInvoiceData.invoiceRemainingBalance) / Number(numPayments)).toFixed(2)
                      const hasDiscount = chargeableTotal < Number(customerData.customerInvoiceData.invoiceRemainingBalance)
                      const nextDate = paymentType === 'weekly'
                        ? (() => {
                          const weeklyDate = new Date(paymentDate)
                          weeklyDate.setDate(paymentDate.getDate() + i * 7)
                          return weeklyDate
                        })()
                        : getMonthlyInstallmentDate(paymentDate, i)

                      return (
                        <li key={i} className='flex justify-between items-center'>
                          <span>Payment {i + 1} - {nextDate.toLocaleDateString()}</span>
                          <div className='flex gap-2 items-center'>
                            {hasDiscount && (
                              <span className='text-gray-400 line-through'>${originalAmount}</span>
                            )}
                            <span className={`font-semibold ${hasDiscount ? 'text-green-600' : 'text-gray-800'}`}>${amount}</span>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
            )}
            <button
              type='submit'
              onClick={handleUploadPaymentInstallations}
              className='w-full text-white p-2 rounded-md font-semibold mt-4 bg-primaryColor hover:bg-primaryDarkColor transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed'
              disabled={uploadingPaymentInstallations || !paymentType || !numPayments || !paymentDate}
            >
              {uploadingPaymentInstallations ? 'Submitting Payment Installments...' : 'Submit Payment Installments'}
            </button>
          </div>
        ) : (
          <div className='bg-white shadow-lg rounded-xl p-6 w-full max-w-lg text-center'>
            <p className='text-lg font-medium text-gray-800 mb-4'>Your payment installment plan has been successfully uploaded!</p>
            <p className='text-sm text-gray-600 mb-6'>You'll be billed according to the schedule you selected.</p>
            <button
              onClick={() => window.location.assign('/dashboard')}
              className='w-full text-white p-2 rounded-md font-semibold bg-primaryColor hover:bg-primaryDarkColor transition duration-200'
            >
              Return to Dashboard
            </button>
          </div>
        )
      )}
    </div>
  )
}